import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { Button, Panel, SectionLabel, Stat } from '@/components/ui'
import { ADDRESSES, CHAIN_ID, IS_LIVE } from '@/lib/contracts'
import distributorAbi from '@/lib/abis/HorizonRevenueDistributor.json'
import { fmt, fmtBalance, fmtBalanceWei, fmtUsd, fmtPct, toNumber, truncAddr, validateAmount, weiToInputString, POLL_SLOW } from '@/lib/utils'
import { useContractTx } from '@/hooks/useContractTx'
import { useProtocolState } from '@/hooks/useProtocolState'
import { EmptyState } from '@/components/dashboard/EmptyState'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { useRevenueShare } from '@/hooks/useRevenueShare'
import { useSnapshotProposals, SNAPSHOT_SPACE, SNAPSHOT_NOT_CONFIGURED } from '@/hooks/useSnapshotProposals'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { LockPanel } from '@/components/dashboard/LockPanel'
import { VotePanel } from '@/components/dashboard/VotePanel'
import { FeeClaimPanel } from '@/components/dashboard/FeeClaimPanel'
import { BribePanel } from '@/components/dashboard/BribePanel'
import { isFeatureEnabled } from '@/lib/features'

const YEAR_SECONDS = 365 * 24 * 60 * 60

/// Known-asset display names for the revenue-share panel below - the assets RevenueRouter is wired
/// to pay lockers natively as whitelisted (WETH + USDx from HorizonLendingPool's revenue legs - see
/// RevenueRouter.sol's own doc comment on the whitelisted-asset tier). Anything else falls back to a
/// truncated address rather than guessing.
function revenueAssetSymbol(asset) {
  const a = (asset || '').toLowerCase()
  if (a === (ADDRESSES.WETH || '').toLowerCase()) return 'ETH'
  if (a === (ADDRESSES.USDX || '').toLowerCase()) return 'USDx'
  if (a === (ADDRESSES.HORIZON_TOKEN || '').toLowerCase()) return 'HORIZON'
  if (a === (ADDRESSES.WBTC || '').toLowerCase()) return 'WBTC'
  if (a === (ADDRESSES.WSTETH || '').toLowerCase()) return 'wstETH'
  if (a === '0xf19308f923582a6f7c465e5ce7a9dc1bec6665b1') return 'TITANX'
  return asset ? truncAddr(asset) : ''
}

/// Same three known assets, as an icon - real token images for all three now (HORIZON reuses the
/// same nav-logo art, src/assets/tokens/horizon.webp - a plain copy, not a shared import, since the
/// nav logo lives in public/ for absolute-URL serving while token icons import from src/assets/
/// like ETH/USDx already do).
/// Resolves the address to a symbol and hands off to TokenGlyph rather than keeping its own image
/// lookup.
///
/// To be precise about what this does and does not fix: stakers only ever receive an asset NATIVELY
/// when it is whitelisted on RevenueRouter, and only WETH and HORIZON are (plus USDx, which needs no
/// swap). Everything else takes the long-tail path and is swapped to USDx wholesale, so WBTC and
/// wstETH do not reach this component today - an earlier version of this comment claimed they did,
/// and that was wrong.
///
/// It is still worth not keeping a second resolver here. The old one knew exactly three addresses
/// and rendered a BLANK circle for anything else, so the day genesis whitelists another reserve -
/// entirely plausible now that WBTC and wstETH generate borrow-fee revenue - stakers would see an
/// empty hole rather than an icon. TokenGlyph carries the full table plus a deterministic letter
/// fallback, so an unknown asset reads as an initial instead of as nothing.
function RevenueAssetIcon({ asset }) {
  return <TokenGlyph symbol={revenueAssetSymbol(asset)} size={40} />
}
const iconStyle = { width: 26, height: 26, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }

/// The asset revenue share pays out in - shown as a fixed preview regardless of on-chain state, so a
/// visitor sees what they will earn before ever staking, not just whichever assets happen to have
/// already accrued a nonzero amount. RevenueRouter settles ALL protocol revenue to its buyback asset
/// (WETH) and routes the protocol's own tokens (HORIZON, hUSD) to treasury, so stakers receive WETH
/// and only WETH - USDx and HORIZON were on this list under an older multi-asset design and are gone.
/// Real amounts merge in once connected; should the router ever pay a second asset natively, it still
/// appends at the end rather than being silently dropped (see the extraAssets merge below).
const EXPECTED_REVENUE_ASSETS = [(ADDRESSES.WETH || '').toLowerCase()]

// Uniswap's own app, not an embedded widget - HORIZON's only market is the on-chain HORIZON/USDx
// pool this protocol's own Farm provides liquidity to, so linking straight to it needs no
// quoting/routing logic of our own to build or maintain. Only chains Uniswap's web app
// actually serves get a link; local/unrecognized chains (dev) get no Buy panel at all.
const UNISWAP_CHAIN_SLUG = { 1: 'ethereum', 8453: 'base', 42161: 'arbitrum', 10: 'optimism', 137: 'polygon' }
const BUY_HORIZON_URL = UNISWAP_CHAIN_SLUG[CHAIN_ID]
  ? `https://app.uniswap.org/swap?outputCurrency=${ADDRESSES.HORIZON_TOKEN}&inputCurrency=${ADDRESSES.WETH}&chain=${UNISWAP_CHAIN_SLUG[CHAIN_ID]}`
  : null

/// ONE product since entry 182: lock HORIZON. The lock IS the stake - time-locked (or burned)
/// HORIZON earns the protocol revenue share (RevenueRouter's staker cut, paid per week by
/// HorizonRevenueDistributor pro-rata to each locker's decayed ve power - entry 312) and votes on
/// where emissions flow. The retired HorizonStaking's flat HORIZON APY is gone with the contract;
/// revenue share is the real-yield leg. (A veCRV-style emission boost was considered and removed
/// pre-launch, entry 305 - fees-follow-votes rewards lockers directly, so the boost was redundant.)
export function StakeSection() {
  const { address: user } = useAccount()
  const { d, deployed, refetch } = useProtocolState()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()

  const totalLockedUsd = hasHorizonPrice ? d.totalLocked * horizonUsd : 0

  return (
    <div id="stake">
      <SectionHeader
        title="Stake"
        subtitle="Lock HORIZON to earn a share of protocol revenue and vote on where emissions flow."
      />

      {!deployed ? (
        // Not deployed on the connected network (pre-launch, or a wrong-network wallet): one calm
        // not-live panel instead of a "0.00 HORIZON locked" hero and a lock form that would revert.
        // The lock, revenue share, and governance appear as soon as staking is live on this network.
        <EmptyState
          title="Staking is not live on this network yet."
          body="Point your wallet at a network where Horizon is deployed. Locking, revenue share, and governance appear here as soon as it is." />
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        {/* Two protocol-wide headline figures: HORIZON committed vs HORIZON permanently destroyed. The
            big HeroFigure was dropped (it duplicated Total locked), and "Your lock" moved out - it's
            already shown as "Your locked HORIZON" inside the Lock panel below. */}
        <div style={heroStatsRow}>
          <Stat boxed label="Total locked" value={`${fmt(d.totalLocked, 2)} HORIZON`} sub={hasHorizonPrice ? fmtUsd(totalLockedUsd) : undefined} />
          <Stat boxed label="Total burned" value={`${fmt(d.totalBurned, 2)} HORIZON`} sub={hasHorizonPrice ? fmtUsd(d.totalBurned * horizonUsd) : undefined} />
        </div>

        {/* ── Lock (wide main) + supporting rail, side by side (collapses to one column under 900px
             via page-grid). LockPanel is the product and NEEDS the width for its amount input +
             duration pills - forcing it to a half-column clustered them. The rail holds the short
             secondary panels stacked (RevenueSharePanel - RevenueRouter's staker leg, entry 182,
             always shown as proof the stream exists before a first lock - then Buy HORIZON), so the
             narrow side fills instead of leaving dead space beside the tall Lock panel. ── */}
        <div className="page-grid" style={lockRailGrid}>
          <LockPanel d={d} onTx={refetch} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 28, minWidth: 0 }}>
            <RevenueSharePanel user={user} onTx={refetch} />

            {BUY_HORIZON_URL && (
              <Panel style={{ padding: 32 }}>
                <SectionLabel>Buy HORIZON</SectionLabel>
                <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body }}>
                  Swap ETH for HORIZON on Uniswap, then lock it above.
                </div>
                <div style={{ marginTop: 16 }}>
                  <a href={BUY_HORIZON_URL} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                    <Button style={{ padding: '14px 18px', fontSize: T.fs.ui }}>Buy HORIZON on Uniswap ↗</Button>
                  </a>
                </div>
              </Panel>
            )}
          </div>
        </div>

        {/* ── ve(3,3) surfaces: gauge vote, then the two claim streams that follow from voting
             (vault fees, bribes). Gated behind the `ve33` flag (VE33-FRONTEND-PLAN.md) so they stay
             hidden until real vault gauges exist, keeping empty gauge/claim lists off the launch
             page. The pooled-revenue claim (RevenueSharePanel, above) is a separate stream and is
             always shown. ── */}
        {isFeatureEnabled('ve33') && (
          <>
            <VotePanel d={d} onTx={refetch} />
            <FeeClaimPanel onTx={refetch} />
            <BribePanel d={d} onTx={refetch} />
          </>
        )}

        {/* ── Governance: vote to earn points, not real decision-making power - see
             GovernancePanel's own doc comment ── */}
        <GovernancePanel totalStaked={d.totalLocked} />
      </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Revenue share - the pooled NON-vault revenue stream (vault trading fees follow the gauge vote via
// FeeDistributor, claimed in the Vault fees panel). Funded by RevenueRouter's locker leg (28% of
// each non-vault revenue source: borrow fees, liquidation cuts, reserve interest), settled to WETH
// before it reaches lockers - see RevenueRouter.sol's own doc comment. Since the redesign (entry
// 312) the payer is the Curve-style HorizonRevenueDistributor, not the ve's old streaming leg: it
// keeps no enumerable on-chain asset list, so the asset set is config-driven (useRevenueShare's
// REVENUE_ASSETS = WETH at launch). Batch-claims across all of them in one call via `claimMany`,
// matching the contract's own shape.
// ─────────────────────────────────────────────────────────────────

function RevenueSharePanel({ user, onTx }) {
  const tx = useContractTx()
  const { assets, pendingByAsset, hasAnyClaimable, refetchPending } = useRevenueShare(user)

  // Always shows all three expected assets (icon + symbol), even before connecting or before
  // anything has accrued - a visitor sees exactly what revenue share pays out in, not just an
  // empty "nothing yet" message. Real pending amounts merge in per-asset once known; anything
  // RevenueRouter has notified beyond the expected three (shouldn't happen today, but not
  // silently dropped if it ever does) appends after them.
  const pendingByAssetLower = new Map(pendingByAsset.map(({ asset, amount }) => [(asset || '').toLowerCase(), amount]))
  const extraAssets = pendingByAsset.filter(({ asset }) => !EXPECTED_REVENUE_ASSETS.includes((asset || '').toLowerCase()))
  const displayRows = [
    ...EXPECTED_REVENUE_ASSETS.map((asset) => ({ asset, amount: pendingByAssetLower.get(asset) ?? 0n })),
    ...extraAssets,
  ]

  async function claimAll() {
    await tx.run(
      { address: ADDRESSES.HORIZON_REVENUE_DISTRIBUTOR, abi: distributorAbi, functionName: 'claimMany', args: [assets] },
      { successMsg: 'Revenue share claimed.' },
    )
    refetchPending().catch((e) => console.error('refetch after claimMany tx failed:', e))
    onTx?.()?.catch?.((e) => console.error('refetch after claimMany tx failed:', e))
  }

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Revenue Share</SectionLabel>
      <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
        Earn your share of protocol revenue from borrowing, liquidations, and reserve interest. Vault
        trading fees follow your gauge vote and are claimed in Vault fees.
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column' }}>
        {displayRows.map(({ asset, amount }) => (
          <div key={asset} style={revenueRow}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <RevenueAssetIcon asset={asset} />
              <span style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700 }}>
                {revenueAssetSymbol(asset)}
              </span>
            </div>
            <span className="mono" style={{ color: amount > 0n ? T.positive : T.textDim, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700 }}>
              {user ? `${fmtBalance(toNumber(amount), 6)} ${revenueAssetSymbol(asset)}` : '—'}
            </span>
          </div>
        ))}
        {!user && (
          <div style={{ marginTop: 10, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
            Connect a wallet to view your own pending amounts.
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <Button variant="ghost" onClick={claimAll} disabled={!user || tx.pending || !hasAnyClaimable} style={{ width: '100%', padding: '16px 18px', fontSize: T.fs.ui }}>
          {tx.pending ? 'Working…' : 'Claim all'}
        </Button>
      </div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Governance - deliberately "vote to earn points," not real decision-making power over protocol
// parameters (confirmed directly with the user before building this - see the plan this shipped
// under). Voting itself happens entirely off-chain via Snapshot.org: genesis creates a Snapshot
// space and points its voting-power strategy at HorizonVoteEscrow's own balanceOf - ve power (both
// off-platform, no code here), so "staking gives you voting weight" is true without a single new
// contract. This panel only reads and displays proposals (useSnapshotProposals, Snapshot's own
// public GraphQL API); casting an actual vote happens on Snapshot's own site (a real off-chain
// signature Snapshot's app already handles correctly - not reimplemented here), linked out to
// below. Every vote also earns a flat points bonus, tallied by scripts/indexPoints.cjs
// independently of this panel. Shows a "not live yet" state until VITE_SNAPSHOT_SPACE is set (see
// useSnapshotProposals.js's own doc comment on SNAPSHOT_NOT_CONFIGURED) - genesis hasn't created
// the space yet - rather than rendering nothing, matching FullPortfolioPanel's own
// notConfigured-gets-an-explicit-message convention (PortfolioSection.jsx) instead of a silent gap.
// ─────────────────────────────────────────────────────────────────

function GovernancePanel({ totalStaked }) {
  const { proposals, loading } = useSnapshotProposals()

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Governance</SectionLabel>
      <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
        Vote on proposals with your veHORIZON power. Longer locks weigh more, and every vote earns
        bonus points.
      </div>

      {SNAPSHOT_NOT_CONFIGURED ? (
        <div style={{ marginTop: 20, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body }}>
          Snapshot voting is coming soon. Your power is already accruing.
        </div>
      ) : (
        <>
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <Stat size="sm" label="Total locked (voting weight source)" value={`${fmt(totalStaked, 2)} HORIZON`} />
          </div>

          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? (
              <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body }}>Loading proposals…</div>
            ) : proposals.length === 0 ? (
              <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body }}>No proposals yet.</div>
            ) : proposals.map((p) => <ProposalRow key={p.id} proposal={p} />)}
          </div>

          <div style={{ marginTop: 16 }}>
            <a href={`https://snapshot.org/#/${SNAPSHOT_SPACE}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <Button variant="ghost" style={{ width: '100%', padding: '14px 18px', fontSize: T.fs.ui }}>
                Vote on Snapshot ↗
              </Button>
            </a>
          </div>
        </>
      )}
    </Panel>
  )
}

function ProposalRow({ proposal }) {
  const isActive = proposal.state === 'active'
  return (
    <a
      href={`https://snapshot.org/#/${SNAPSHOT_SPACE}/proposal/${proposal.id}`}
      target="_blank" rel="noreferrer"
      style={{ ...revenueRow, textDecoration: 'none', cursor: 'pointer' }}
    >
      <span style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700 }}>{proposal.title}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>{proposal.votes} votes</span>
        <span style={{
          color: isActive ? T.positive : T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption,
          fontWeight: 700, textTransform: 'capitalize',
        }}>{proposal.state}</span>
      </div>
    </a>
  )
}

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────

const heroStatsRow = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20,
}
const lockRailGrid = {
  display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(320px, 1fr)', gap: 28, alignItems: 'start',
}
const revenueRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  padding: '12px 0', borderBottom: `1px solid ${T.border}`,
}
const modeRow = { display: 'flex', gap: 8 }
const inputLabel = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase',
}
const inputBox = {
  width: '100%', background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md,
  padding: '14px 14px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, outline: 'none', boxSizing: 'border-box',
}
